/// Top-level router state.
///
/// Parses the current URL pathname into a `Route` value and exposes the result
/// as a `$state`-backed reactive value. Navigation is performed via
/// `@shared/router/navigation`; this module listens to the browser `popstate`
/// event (back/forward) and the custom `lpcrm:navigate` event dispatched by
/// `navigate()`. Initialization is deferred to `startRouter()` so the module
/// is safe to import in SSR / test environments where `window` is not yet
/// available.

import { NAVIGATE_EVENT } from '@shared/router/navigation';
import { formCrmEnabled } from '@shared/config/features';

export type Route =
  | { name: 'firms-list' }
  | { name: 'firm-detail'; id: string }
  | { name: 'lawyers-list' }
  | { name: 'lawyer-detail'; id: string }
  | { name: 'site-members-list' }
  | { name: 'products-list' }
  | { name: 'product-detail'; id: string }
  | { name: 'forms-list' }
  | { name: 'form-detail'; id: string }
  | { name: 'form-submissions-list' }
  | { name: 'form-submission-detail'; id: string }
  | { name: 'third-party-solutions-list' }
  | { name: 'third-party-solution-detail'; id: string }
  | { name: 'problems-list' }
  | { name: 'problem-detail'; id: string }
  | { name: 'dashboard' }
  | { name: 'admin-organizations' }
  | { name: 'admin-organization-detail'; id: string }
  | { name: 'members' }
  | { name: 'not-found'; path: string };

function readPathname(): string {
  if (typeof window === 'undefined') return '/';
  const raw = window.location.pathname;
  return raw.length === 0 ? '/' : raw;
}

function parsePath(path: string): Route {
  if (path === '' || path === '/' || path === '/firms') {
    return { name: 'firms-list' };
  }
  const firmMatch = path.match(/^\/firms\/([\w_-]+)$/);
  if (firmMatch) {
    return { name: 'firm-detail', id: firmMatch[1]! };
  }
  if (path === '/lawyers') {
    return { name: 'lawyers-list' };
  }
  const lawyerMatch = path.match(/^\/lawyers\/([\w_-]+)$/);
  if (lawyerMatch) {
    return { name: 'lawyer-detail', id: lawyerMatch[1]! };
  }
  // 会員 (公開サイトのマイページ登録者)。社内担当者の `/members` とは別物なので
  // パスも `site-` で分ける (backend の `/api/site-members` と対)。
  if (path === '/site-members') {
    return { name: 'site-members-list' };
  }
  if (path === '/products') {
    return { name: 'products-list' };
  }
  const productMatch = path.match(/^\/products\/([\w_-]+)$/);
  if (productMatch) {
    return { name: 'product-detail', id: productMatch[1]! };
  }
  if (formCrmEnabled) {
    if (path === '/forms') {
      return { name: 'forms-list' };
    }
    const formMatch = path.match(/^\/forms\/([\w_-]+)$/);
    if (formMatch) {
      return { name: 'form-detail', id: formMatch[1]! };
    }
    if (path === '/form-submissions') {
      return { name: 'form-submissions-list' };
    }
    const formSubmissionMatch = path.match(/^\/form-submissions\/([\w_-]+)$/);
    if (formSubmissionMatch) {
      return { name: 'form-submission-detail', id: formSubmissionMatch[1]! };
    }
  }
  if (path === '/third-party-solutions') {
    return { name: 'third-party-solutions-list' };
  }
  const thirdPartySolutionMatch = path.match(/^\/third-party-solutions\/([\w_-]+)$/);
  if (thirdPartySolutionMatch) {
    return { name: 'third-party-solution-detail', id: thirdPartySolutionMatch[1]! };
  }
  if (path === '/master/problems') {
    return { name: 'problems-list' };
  }
  const problemMatch = path.match(/^\/master\/problems\/([\w_-]+)$/);
  if (problemMatch) {
    return { name: 'problem-detail', id: problemMatch[1]! };
  }
  if (path === '/dashboard') {
    return { name: 'dashboard' };
  }
  if (path === '/admin' || path === '/admin/organizations') {
    return { name: 'admin-organizations' };
  }
  const orgDetailMatch = path.match(/^\/admin\/organizations\/([\w@|.+~%_-]+)$/);
  if (orgDetailMatch) {
    return { name: 'admin-organization-detail', id: decodeURIComponent(orgDetailMatch[1]!) };
  }
  // 統合「メンバー」画面 (ACC-7)。旧 `設定>担当者` (/settings/accounts) と
  // `管理>ユーザー(Auth0)` (/admin/users) はここに集約したため、旧パスのブックマークも
  // 統合画面へ寄せる。
  if (
    path === '/members' ||
    path === '/settings' ||
    path === '/settings/accounts' ||
    path === '/admin/users'
  ) {
    return { name: 'members' };
  }
  return { name: 'not-found', path };
}

const state = $state<{ route: Route }>({ route: { name: 'firms-list' } });
let started = false;

/// Initialise the router. Safe to call multiple times.
export function startRouter(): void {
  if (started || typeof window === 'undefined') return;
  state.route = parsePath(readPathname());
  const update = (): void => {
    state.route = parsePath(readPathname());
  };
  window.addEventListener('popstate', update);
  window.addEventListener(NAVIGATE_EVENT, update);
  started = true;
}

export const router = {
  get route(): Route {
    return state.route;
  },
};
